<script setup lang="ts">
import { router, usePage } from '@inertiajs/vue3';
import { ChevronLeft } from 'lucide-vue-next';
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import type { ReferenceItem } from '@/components/references/ReferenceListItem.vue';
import { useEditorOutline } from '@/composables/useEditorOutline';
import { useToast } from '@/composables/useToast';
import EditorSidebarLayout from '@/layouts/EditorSidebarLayout.vue';
import * as projectArticlesRoutes from '@/routes/projects/articles';
import * as documentRoutes from '@/routes/projects/document';
import { print as printRoute } from '@/routes/projects/editor';
import EditorCanvas from './partials/EditorCanvas.vue';
import type { HeadingItem } from './partials/EditorCanvas.vue';
import EditorTopbar from './partials/EditorTopbar.vue';
import SidePanel from './partials/SidePanel.vue';
import type {
    DocumentCommentThreadItem,
    DocumentNoteItem,
    DocumentTaskItem,
} from './partials/tabs/types';

// Re-export for consumers that still import Reference from this module.
export type Reference = ReferenceItem;

defineOptions({ layout: EditorSidebarLayout });

const {
    project,
    document,
    references,
    assets,
    extractionGrid,
    notes: initialNotes,
    tasks: initialTasks,
    commentThreads: initialCommentThreads,
} = defineProps<{
    project: { id: string; name: string; citation_style: string };
    document: {
        id: string;
        title: string;
        content: string | null;
        numbering_enabled: boolean;
        updated_at: string;
    };
    references: ReferenceItem[];
    assets: Array<{
        id: string;
        original_name: string;
        mime: string;
        size_bytes: number;
        caption?: string | null;
        alt?: string | null;
        src: string;
        created_at?: string;
    }>;
    extractionGrid: {
        id: string;
        rows_count: number;
        columns: Array<{ id: string; name: string; type: string }>;
    } | null;
    notes: DocumentNoteItem[];
    tasks: DocumentTaskItem[];
    commentThreads: DocumentCommentThreadItem[];
}>();

const toast = useToast();
const currentUserId = computed(() => usePage().props.auth.user.id);

// ── State ──────────────────────────────────────────────────────────────
const title = ref(document.title);
const saving = ref(false);
const numberingEnabled = ref(document.numbering_enabled);

// ── Focus mode (persisted via localStorage) ────────────────────────────
const FOCUS_KEY = 'editor:focus-mode';
const focusMode = ref(
    typeof window !== 'undefined'
        ? localStorage.getItem(FOCUS_KEY) === 'true'
        : false,
);

// Right panel: hidden when focus mode is on
const showRight = ref(!focusMode.value);

watch(focusMode, (val) => {
    if (typeof window !== 'undefined') {
        localStorage.setItem(FOCUS_KEY, val ? 'true' : 'false');
    }

    showRight.value = !val;
});

// ── Canvas ref ────────────────────────────────────────────────────────
const canvas = ref<InstanceType<typeof EditorCanvas> | null>(null);

// Live set of cited paper IDs (reactive, from EditorCanvas via expose)
const citedIds = ref<ReadonlySet<string>>(new Set());

watch(
    () => canvas.value?.citedIds,
    (ids) => {
        if (ids) {
            citedIds.value = ids;
        }
    },
    { deep: false },
);

// ── Title auto-save ────────────────────────────────────────────────────
let titleDebounce: ReturnType<typeof setTimeout>;

watch(title, (val) => {
    clearTimeout(titleDebounce);
    titleDebounce = setTimeout(() => save({ title: val }), 800);
});

function csrfToken(): string {
    return decodeURIComponent(
        window.document.cookie
            .split('; ')
            .find((c) => c.startsWith('XSRF-TOKEN='))
            ?.split('=')[1] ?? '',
    );
}

/** POST/PATCH/DELETE helper matching this page's existing manual-fetch save() pattern (no full Inertia reload). */
async function api<T = unknown>(
    url: string,
    method: 'POST' | 'PATCH' | 'DELETE',
    body?: unknown,
): Promise<T | null> {
    const res = await fetch(url, {
        method,
        headers: {
            'Content-Type': 'application/json',
            'X-XSRF-TOKEN': csrfToken(),
            Accept: 'application/json',
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (!res.ok) {
        toast.error('Une erreur est survenue. Réessayez.');

        return null;
    }

    if (res.status === 204) {
        return null;
    }

    return (await res.json()) as T;
}

async function save(payload: {
    title?: string;
    content?: string;
    numbering_enabled?: boolean;
}) {
    saving.value = true;

    try {
        await fetch(documentRoutes.update(project.id).url, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'X-XSRF-TOKEN': csrfToken(),
            },
            body: JSON.stringify(payload),
        });
    } finally {
        saving.value = false;
    }
}

function onContentUpdate(html: string) {
    save({ content: html });
}

function toggleNumbering() {
    numberingEnabled.value = !numberingEnabled.value;
    save({ numbering_enabled: numberingEnabled.value });
}

// ── Export ──────────────────────────────────────────────────────────────
function exportPdf() {
    window.open(printRoute.url(project.id), '_blank');
}

async function exportWord() {
    const editorInstance = canvas.value?.editor;

    if (!editorInstance) {
        return;
    }

    const { exportDocumentToDocx } = await import('@/lib/editor/docx');

    await exportDocumentToDocx({
        editor: editorInstance,
        references,
        citationStyle:
            (project.citation_style as 'apa' | 'chicago' | 'vancouver') ??
            'apa',
        citedIds: canvas.value?.citedIds ?? new Set(),
        citedOrder: canvas.value?.citedOrder ?? [],
        assets: assets,
        projectName: project.name,
        documentTitle: title.value,
    });
}

// ── Outline (shared with AppSidebar via composable) ───────────────────
const { headings, onJump } = useEditorOutline();

function onHeadings(items: HeadingItem[]) {
    headings.value = items;
}

function jumpTo(pos: number) {
    canvas.value?.jumpTo(pos);
}

// Register jump callback for AppSidebar to call
onJump.value = jumpTo;

// ── Citation ───────────────────────────────────────────────────────────
function insertCitation(ref: ReferenceItem) {
    const authors = Array.isArray(ref.authors)
        ? ref.authors
        : [ref.authors ?? ''];
    const firstAuthorLastName =
        (authors[0] ?? '').split(' ').pop()?.trim() || 'Auteur';
    const label = `(${firstAuthorLastName}${ref.year ? `, ${ref.year}` : ''})`;
    canvas.value?.insertCitation(ref.id, ref.key, label);
}

// ── Notes ──────────────────────────────────────────────────────────────
const notes = ref<DocumentNoteItem[]>(initialNotes);

async function onAddNote(body: string) {
    const note = await api<DocumentNoteItem>(
        `/projects/${project.id}/document/notes`,
        'POST',
        { body },
    );

    if (note) {
        notes.value = [note, ...notes.value];
    }
}

async function onRemoveNote(id: string) {
    const previous = notes.value;
    notes.value = notes.value.filter((n) => n.id !== id);

    const res = await fetch(`/projects/${project.id}/document/notes/${id}`, {
        method: 'DELETE',
        headers: { 'X-XSRF-TOKEN': csrfToken() },
    });

    if (!res.ok) {
        notes.value = previous;
        toast.error('Impossible de supprimer cette note.');
    }
}

// ── Tasks ──────────────────────────────────────────────────────────────
const tasks = ref<DocumentTaskItem[]>(initialTasks);

async function onAddTask(body: string) {
    const task = await api<DocumentTaskItem>(
        `/projects/${project.id}/document/tasks`,
        'POST',
        { body },
    );

    if (task) {
        tasks.value = [task, ...tasks.value];
    }
}

async function onToggleTask(id: string) {
    const task = await api<DocumentTaskItem>(
        `/projects/${project.id}/document/tasks/${id}/toggle`,
        'PATCH',
    );

    if (task) {
        tasks.value = tasks.value.map((t) => (t.id === id ? task : t));
    }
}

async function onRemoveTask(id: string) {
    const previous = tasks.value;
    tasks.value = tasks.value.filter((t) => t.id !== id);

    const res = await fetch(`/projects/${project.id}/document/tasks/${id}`, {
        method: 'DELETE',
        headers: { 'X-XSRF-TOKEN': csrfToken() },
    });

    if (!res.ok) {
        tasks.value = previous;
        toast.error('Impossible de supprimer cette tâche.');
    }
}

// ── Comments (anchored to a text selection) ─────────────────────────────
const commentThreads = ref<DocumentCommentThreadItem[]>(initialCommentThreads);
const activeSidePanelTab = ref('references');
const pendingCommentAnchor = ref<string | null>(null);
const focusThreadId = ref<string | null>(null);

function onRequestComment(selectedText: string) {
    if (!selectedText.trim()) {
        toast.warning('Sélectionnez du texte à commenter.');

        return;
    }

    pendingCommentAnchor.value = selectedText;
    activeSidePanelTab.value = 'comments';
    showRight.value = true;
}

function onCancelPendingThread() {
    pendingCommentAnchor.value = null;
}

async function onCreateThread(body: string) {
    const thread = await api<DocumentCommentThreadItem>(
        `/projects/${project.id}/document/comments`,
        'POST',
        { body, anchor_text: pendingCommentAnchor.value },
    );

    if (thread) {
        commentThreads.value = [thread, ...commentThreads.value];
        canvas.value?.applyCommentMark(thread.id);
        pendingCommentAnchor.value = null;
    }
}

async function onReplyThread(threadId: string, body: string) {
    const thread = await api<DocumentCommentThreadItem>(
        `/projects/${project.id}/document/comments/${threadId}/reply`,
        'POST',
        { body },
    );

    if (thread) {
        commentThreads.value = commentThreads.value.map((t) =>
            t.id === threadId ? thread : t,
        );
    }
}

async function onResolveThread(threadId: string) {
    const thread = await api<DocumentCommentThreadItem>(
        `/projects/${project.id}/document/comments/${threadId}/resolve`,
        'PATCH',
    );

    if (thread) {
        commentThreads.value = commentThreads.value.map((t) =>
            t.id === threadId ? thread : t,
        );
    }
}

async function onReopenThread(threadId: string) {
    const thread = await api<DocumentCommentThreadItem>(
        `/projects/${project.id}/document/comments/${threadId}/reopen`,
        'PATCH',
    );

    if (thread) {
        commentThreads.value = commentThreads.value.map((t) =>
            t.id === threadId ? thread : t,
        );
    }
}

async function onRemoveThread(threadId: string) {
    const previous = commentThreads.value;
    commentThreads.value = commentThreads.value.filter(
        (t) => t.id !== threadId,
    );

    const res = await fetch(
        `/projects/${project.id}/document/comments/${threadId}`,
        { method: 'DELETE', headers: { 'X-XSRF-TOKEN': csrfToken() } },
    );

    if (res.ok) {
        canvas.value?.removeCommentMark(threadId);
    } else {
        commentThreads.value = previous;
        toast.error('Impossible de supprimer ce fil de commentaires.');
    }
}

/** A comment highlight was clicked in the document — jump to its thread, forcing the watcher even if the same thread is clicked twice in a row. */
async function onOpenThread(commentId: string) {
    activeSidePanelTab.value = 'comments';
    showRight.value = true;
    focusThreadId.value = null;
    await nextTick();
    focusThreadId.value = commentId;
}

// ── Responsive: collapse right panel below lg ─────────────────────────
function applyResponsive() {
    if (!focusMode.value) {
        showRight.value = window.matchMedia('(min-width: 1024px)').matches;
    }
}

onMounted(() => {
    applyResponsive();
    window.addEventListener('resize', applyResponsive);
});

onUnmounted(() => {
    window.removeEventListener('resize', applyResponsive);
    // Clear outline when leaving the editor
    headings.value = [];
    onJump.value = null;
});
</script>

<template>
    <div class="flex flex-1 flex-col overflow-hidden">
        <!-- Editor-specific topbar (replaces AppSidebarHeader for this page) -->
        <EditorTopbar
            v-model:title="title"
            :project-name="project.name"
            :saving="saving"
            :show-right="showRight"
            :focus-mode="focusMode"
            :numbering-enabled="numberingEnabled"
            @back="router.visit(projectArticlesRoutes.index(project.id).url)"
            @toggle-right="showRight = !showRight"
            @toggle-focus="focusMode = !focusMode"
            @toggle-numbering="toggleNumbering"
            @export-pdf="exportPdf"
            @export-word="exportWord"
        />

        <!-- 2-column body: editor canvas + side panel -->
        <div class="relative flex min-h-0 flex-1 overflow-hidden">
            <!-- Zone centrale : éditeur -->
            <EditorCanvas
                ref="canvas"
                class="min-w-0 flex-1"
                data-tour="editor-canvas"
                :initial-content="document.content ?? ''"
                :references="references"
                :citation-style="project.citation_style"
                :numbering-enabled="numberingEnabled"
                :project-id="project.id"
                :assets="assets"
                :extraction-grid="extractionGrid"
                @update="onContentUpdate"
                @headings="onHeadings"
                @request-comment="onRequestComment"
                @open-thread="onOpenThread"
            />

            <!-- Panneau droit (5 onglets) -->
            <Transition
                enter-active-class="transition-all duration-200"
                enter-from-class="translate-x-full opacity-0"
                leave-active-class="transition-all duration-200"
                leave-to-class="translate-x-full opacity-0"
            >
                <div
                    v-show="showRight"
                    class="fixed inset-y-0 right-0 z-30 w-80 shadow-lg lg:static lg:z-auto lg:flex lg:shrink-0 lg:shadow-none"
                >
                    <SidePanel
                        v-model:active-tab="activeSidePanelTab"
                        :references="references"
                        :cited-ids="citedIds"
                        :current-user-id="currentUserId"
                        :notes="notes"
                        :tasks="tasks"
                        :comment-threads="commentThreads"
                        :pending-comment-anchor="pendingCommentAnchor"
                        :focus-thread-id="focusThreadId"
                        @cite="insertCitation"
                        @add-note="onAddNote"
                        @remove-note="onRemoveNote"
                        @add-task="onAddTask"
                        @toggle-task="onToggleTask"
                        @remove-task="onRemoveTask"
                        @create-thread="onCreateThread"
                        @cancel-pending-thread="onCancelPendingThread"
                        @reply-thread="onReplyThread"
                        @resolve-thread="onResolveThread"
                        @reopen-thread="onReopenThread"
                        @remove-thread="onRemoveThread"
                    />
                </div>
            </Transition>

            <!-- Backdrop mobile droite -->
            <div
                v-if="showRight"
                class="fixed inset-0 z-20 bg-black/20 lg:hidden"
                @click="showRight = false"
            />

            <!-- Poignée pour rouvrir le panneau (mode concentré) -->
            <button
                v-if="!showRight"
                class="absolute top-1/2 right-0 z-10 hidden -translate-y-1/2 flex-col items-center gap-1.5 rounded-l-md border border-r-0 border-border bg-muted px-1.5 py-3 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground lg:flex"
                aria-label="Afficher le panneau latéral"
                @click="showRight = true"
            >
                <ChevronLeft class="h-3.5 w-3.5 shrink-0" />
                <span
                    class="text-[11px] tracking-wide [writing-mode:vertical-lr]"
                    >Panneau</span
                >
            </button>
        </div>
    </div>
</template>
